import stateService from "./stateService";
import oidcService from "./oidcService";
import workSpacesService from "./workSpaceService";
import organizationService from "./organizationService";
import { states, stateEvents } from "../models/states";

// The active workspace, per account: userInfo does not carry it (an hrms
// account's userInfo.workspaceId and workspaceExposedId are always null)
const ACTIVE_KEY = "validatege.workspace";
const quiet = { skipCustomErrorHandling: true };

// The signed-in session's workspaces and organizations (share-prompt task 16,
// backend multi-tenancy runbook Stage 4b). A corporate (`hrms`) login has no
// B2C main workspace: it works in its organizations' primary workspaces, and
// with none active the dashboard asks to pick an organization. Published as
// the `allWorkSpaces` state: { workspaces, organizations }.
class SessionService {
  constructor() {
    this.workspaces = [];
    this.organizations = [];
    this.mainWorkspaceId = null;

    // any call: the person left an organization (or it is no longer
    // enrolled) - read the lists again, drop it from the switcher
    stateService.onEvent(stateEvents.organizationMembershipLost).subscribe(() => {
      this.reload().catch(() => {});
    });
    // any call: Keycloak cannot be read now; the backend's Keycloak call
    // needs a live SSO session, so renew it. Never a sign-out.
    stateService.onEvent(stateEvents.organizationsUnavailable).subscribe(() => {
      this.renew();
    });
  }

  isCorporate = () => oidcService.activeRealm() === "corp";

  // the organization whose primary workspace this is (its exposed id), or null
  organizationOf = (workspaceExposedId) => organizationService.ofWorkspace(this.organizations, workspaceExposedId);

  // ---- loading ---------------------------------------------------------

  // the workspaces, and for a corporate login its organizations; rejects
  // with the call's error (the session-start page shows it)
  load = async () => {
    const [workspaces, organizations] = await Promise.all([
      workSpacesService.getWorkSpaces(quiet).toPromise(),
      this.isCorporate() ? organizationService.list().toPromise() : Promise.resolve({ data: [] }),
    ]);
    this.workspaces = workspaces.data || [];
    this.organizations = organizations.data || [];
    stateService.setState(states.allWorkSpaces, {
      workspaces: this.workspaces,
      organizations: this.organizations,
    });
  };

  // a workspace of the list, by its exposed id (the website's only workspace id)
  listed = (exposedId) => exposedId != null && this.workspaces.some((w) => w.exposedId === exposedId);

  // whether the caller owns the workspace (its listed key: owner or CAN_DELETE;
  // a joined one carries a role)
  owns = (exposedId) =>
    exposedId != null && this.workspaces.some((w) => w.exposedId === exposedId && (w.key === "owner" || w.key === "CAN_DELETE"));

  // after a session-start userInfo: the lists, and userInfo.workspaceExposedId
  // set to the active workspace (the remembered one while it is still listed,
  // else the B2C main one; null for a corporate login with none yet). Every
  // workspace id here is the exposed one (2026-10-13).
  start = async (userInfo) => {
    await this.load();
    // the B2C main workspace (null for a corporate login): where a reload
    // falls back to when the active one is no longer listed
    this.mainWorkspaceId = userInfo.workspaceExposedId != null ? userInfo.workspaceExposedId : null;
    const remembered = this.remembered();
    let active = null;
    if (this.listed(remembered)) active = remembered;
    else if (this.mainWorkspaceId != null) active = this.mainWorkspaceId;
    return { ...userInfo, workspaceExposedId: active };
  };

  // the lists again; an active workspace no longer listed (left, removed,
  // its organization gone) gives way to the main one (B2C) or to the
  // organization picker (corporate)
  reload = async () => {
    await this.load();
    const info = stateService.getState(states.userInfo).value;
    const active = info && info.workspaceExposedId;
    if (active != null && !this.listed(active))
      this.switchTo(this.listed(this.mainWorkspaceId) ? this.mainWorkspaceId : null);
  };

  // ---- switching -------------------------------------------------------

  // the event first, then userInfo (the dashboard's event handler starts
  // the list; its userInfo subscription then finds a fetch running)
  switchTo = (workspaceExposedId) => {
    this.remember(workspaceExposedId);
    stateService.event(states.changeWorkSpace, workspaceExposedId);
    const info = { ...(stateService.getState(states.userInfo).value || {}), workspaceExposedId };
    stateService.setState(states.userInfo, info);
  };

  // into an enrolled organization: its primary workspace, made by the init
  // the first time (the caller asked "first time in {name}" before). Rejects
  // with the call's error: 403 ORGANIZATION_MEMBERSHIP_REQUIRED (the lists are
  // read again), 404 (no longer enrolled), 503 ORGANIZATIONS_UNAVAILABLE.
  enterOrganization = async (organization) => {
    if (organization.primaryWorkspaceExposedId != null) {
      this.switchTo(organization.primaryWorkspaceExposedId);
      return organization.primaryWorkspaceExposedId;
    }
    try {
      const res = await organizationService.init(organization.organizationId).toPromise();
      await this.load();
      this.switchTo(res.data.workspaceExposedId);
      return res.data.workspaceExposedId;
    } catch (e) {
      const status = e && e.response && e.response.status;
      if (status === 404) this.reload().catch(() => {});
      throw e;
    }
  };

  // ---- the remembered workspace ------------------------------------------

  _key = () => {
    const user = oidcService.currentUser();
    const sub = user && user.profile && user.profile.sub;
    return sub ? `${ACTIVE_KEY}.${oidcService.activeRealm()}.${sub}` : null;
  };

  // the exposed id, as a string; one remembered before 2026-10-13 is numeric,
  // matches no workspace, and the main one is used instead
  remembered = () => {
    const key = this._key();
    try {
      const value = key && localStorage.getItem(key);
      return value || null;
    } catch (e) {
      return null;
    }
  };

  remember = (workspaceExposedId) => {
    const key = this._key();
    if (!key) return;
    try {
      if (workspaceExposedId != null) localStorage.setItem(key, String(workspaceExposedId));
      else localStorage.removeItem(key);
    } catch (e) {
      /* storage unavailable: the main workspace again next time */
    }
  };

  // ---- 503 ORGANIZATIONS_UNAVAILABLE ---------------------------------------

  // renew the Keycloak session; a failed renewal means a new login of the
  // same realm, back to this page (never a sign-out)
  renew = () =>
    oidcService.refreshSingleFlight(true).then(
      (user) => user,
      () =>
        oidcService.login({
          realm: oidcService.activeRealm() || "b2c",
          returnTo: window.location.pathname + window.location.search,
          tabOnly: oidcService.isTabScoped(),
        })
    );
}

export default new SessionService();
